import { PhotographIcon } from '@heroicons/react/outline';
import React, { ChangeEvent, useContext, useRef, useState } from 'react';
import { MeContext } from '../contexts/me.context';
import uploadService from '../../services/upload.service';

type Props = {
  label: string;
  name: string;
  value?: string;
  onChange: (url: string) => void;
  className?: string;
};

const SanityImageUploader: React.FC<Props> = ({
  label,
  name,
  value,
  onChange,
  className = '',
}) => {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { me } = useContext(MeContext);

  const onChangeFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const accountId = me?.account?.id;
    if (accountId === undefined || accountId === null) {
      setError('Could not determine the account for this upload.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      // Uploads go through the backend so the Sanity write token stays server side.
      const url = await uploadService.uploadBlogImageToSanity(accountId, file);
      if (!url) throw new Error('No URL returned from Sanity.');
      onChange(url);
    } catch (err: any) {
      setError(err?.message || 'Upload failed. Please try again.');
      console.error('Sanity image upload failed:', err);
    } finally {
      setUploading(false);
      // Reset input so the same file can be re-selected if needed
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className={className}>
      <label
        htmlFor={name}
        className="block text-sm font-medium leading-5 text-gray-900 sm:mt-px sm:pt-2"
      >
        {label}
      </label>
      <div className="mt-1 flex items-center gap-4">
        {/* Thumbnail preview */}
        <span className="h-16 w-16 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
          {value ? (
            <img
              className="object-cover w-full h-full"
              src={value}
              alt={label}
            />
          ) : (
            <PhotographIcon className="h-full w-full text-gray-300 p-2" />
          )}
        </span>

        <div className="flex flex-col gap-1.5">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={uploading}
              className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              onClick={() => input.current?.click()}
            >
              {uploading ? 'Uploading…' : value ? 'Change' : 'Upload image'}
            </button>
            {value && (
              <button
                type="button"
                className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => onChange('')}
              >
                Remove
              </button>
            )}
          </div>
          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}
          {value && (
            <p className="text-xs text-gray-400 truncate max-w-xs">{value}</p>
          )}
        </div>
      </div>
      <input
        ref={input}
        onChange={onChangeFile}
        accept="image/*"
        multiple={false}
        type="file"
        hidden
      />
    </div>
  );
};

export default SanityImageUploader;
